import React, { useState, useMemo, useEffect } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { lighten, makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import { Icon, TextField, Grid, InputLabel, FormControl, Select, MenuItem } from '@material-ui/core';
import { cleanDemand, chooseDemand, loadDemands } from '../../redux/actions/demandActions'
import ConfirmDeleteDemand from './ConfirmDeleteDemand';
import ConfirmFinishDemand from './ConfirmFinishDemand';
import MaskedInput from 'react-text-mask';

function desc(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function stableSort(array, cmp) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = cmp(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map(el => el[0]);
}

function getSorting(order, orderBy) {
    return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

const headRows = [
    { id: 'customerName', numeric: false, disablePadding: false, label: 'Customer Name' },
    { id: 'customerContact', numeric: false, disablePadding: false, label: 'Customer Phone Number' },
    { id: 'state', numeric: false, disablePadding: false, label: 'State' },
    { id: 'category', numeric: false, disablePadding: false, label: 'Category' },
    { id: 'creationDate', numeric: false, disablePadding: false, label: 'Creation Date' },
    { id: 'finished', numeric: false, disablePadding: false, label: 'Finished' },
    { id: 'actions', numeric: false, disablePadding: false, label: 'Actions' }
];

const formatDateToAmerican = (dateString) => {
    const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
    return new Date(dateString).toLocaleDateString('en-US', options);
};

function EnhancedTableHead(props) {
    const { order, orderBy, onRequestSort } = props;
    const createSortHandler = property => event => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {headRows.map(row => (
                    <TableCell
                        key={row.id}
                        align={row.numeric ? 'right' : 'left'}
                        padding={row.disablePadding ? 'none' : 'default'}
                        sortDirection={orderBy === row.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === row.id}
                            direction={order}
                            onClick={createSortHandler(row.id)}
                        >
                            {row.label}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.string.isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

const useToolbarStyles = makeStyles(theme => ({
    root: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(1),
    },
    highlight: theme.palette.type === 'light'
        ? {
            color: theme.palette.secondary.main,
            backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
        : {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.secondary.dark,
        },
    spacer: {
        flex: '1 1 100%',
    },
    actions: {
        color: theme.palette.text.secondary,
    },
    title: {
        flex: '0 0 auto',
    },
}));

const EnhancedTableToolbar = props => {
    const classes = useToolbarStyles();
    const { numSelected } = props;

    return (
        <Toolbar
            className={clsx(classes.root, {
                [classes.highlight]: numSelected > 0,
            })}
        >
            <div className={classes.title}>
                {numSelected > 0 ? (
                    <Typography color="inherit" variant="subtitle1">
                        {numSelected} selected
                    </Typography>
                ) : (
                    <Typography variant="h6" id="tableTitle">
                        Demands
                    </Typography>
                )}
            </div>
            <div className={classes.spacer} />
            <div className={classes.actions}>
                <Tooltip title="Add new Demand">
                    <IconButton color="secondary" aria-label="Add new Demand" onClick={props.addDemand}>
                        <Icon>add</Icon>
                    </IconButton>
                </Tooltip>
            </div>
        </Toolbar>
    );
};

EnhancedTableToolbar.propTypes = {
    numSelected: PropTypes.number.isRequired,
};

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%'
    },
    paper: {
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    table: {
        minWidth: 750,
    },
    tableWrapper: {
        overflowX: 'auto',
    },
    search: {
        margin: theme.spacing(1),
        width: '90%'
    }
}));

function DemandsTable(props) {
    const classes = useStyles();
    const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);
    const [showFinishConfirmation, setShowFinishConfirmation] = React.useState(false);
    const [selectedDemand, setSelectedDemand] = React.useState(null);
    const [order, setOrder] = React.useState('desc');
    const [orderBy, setOrderBy] = React.useState('creationDate');
    const [selected, setSelected] = React.useState([]);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);
    const [customerNameSearch, setCustomerNameSearch] = useState('');
    const [categorySearch, setCategorySearch] = useState(''); // para categoria
    const [finishSearch, setFinishSearch] = useState(null); // para isActive
    const [dateSearch, setDateSearch] = useState(null); // para creationDate
    const [monthSearch, setMonthSearch] = useState(''); // para o mês
    const [yearSearch, setYearSearch] = useState(''); // para o ano
    const [selectedMonth, setSelectedMonth] = useState(''); // para o mês
    const [selectedYear, setSelectedYear] = useState(''); // para o ano

    function showModalDelete(demand) {
        setShowDeleteConfirmation(true);
        setSelectedDemand(demand);
    }

    function closeModalDelete() {
        setShowDeleteConfirmation(false);
        setSelectedDemand(null);
    }

    function showModalFinish(demand) {
        setShowFinishConfirmation(true);
        setSelectedDemand(demand);
    }

    function closeModalFinish() {
        setShowFinishConfirmation(false);
        setSelectedDemand(null);
    }

    function handleRequestSort(event, property) {
        const isDesc = orderBy === property && order === 'desc';
        setOrder(isDesc ? 'asc' : 'desc');
        setOrderBy(property);
    }

    function handleSelectAllClick(event) {
        if (event.target.checked) {
            const newSelecteds = props.demands.map(n => n.customerName);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    }

    function handleChangePage(event, newPage) {
        setPage(newPage);
    }

    function handleChangeRowsPerPage(event) {
        setRowsPerPage(+event.target.value);
        setPage(0);
    }

    function handleFilter() {
        if (customerNameSearch === '' && categorySearch === '' && finishSearch === null
            && selectedMonth === '' && selectedYear === '') {
            return props.demands;
        } else {
            return props.demands.filter((demand) => {
                let matchesSearchCustomerName = true;
                let matchesCategory = true;
                let matchesFinish = true;
                let matchesDate = true;

                if (customerNameSearch !== '') {
                    matchesSearchCustomerName = demand.customerName.toUpperCase().includes(customerNameSearch.toUpperCase());
                }

                if (categorySearch !== '') {
                    matchesCategory = demand.category.toUpperCase().includes(categorySearch.toUpperCase());
                }

                if (finishSearch !== null) {
                    matchesFinish = (demand.finished === true && finishSearch === 'Yes') ||
                        (demand.finished === false && finishSearch === 'No');
                }

                if (selectedMonth !== '' && selectedYear !== '') {
                    let creationDate = demand.creationDate.split('T')[0]; // get the date part of the string
                    let [year, month, day] = creationDate.split('-').map(Number);
                    let demandDate = new Date(Date.UTC(year, month - 1, day));
                    matchesDate = (demandDate.getUTCMonth() + 1 === parseInt(selectedMonth)) &&
                        (demandDate.getUTCFullYear() === parseInt(selectedYear));
                } else {
                    matchesDate = customerNameSearch !== '' || categorySearch !== '' || finishSearch !== null;
                }

                return matchesSearchCustomerName && matchesCategory && matchesFinish && matchesDate;
            });
        }
    }

    let listDemands = handleFilter();

    const isSelected = name => selected.indexOf(name) !== -1;

    const emptyRows = rowsPerPage - Math.min(rowsPerPage, listDemands.length - page * rowsPerPage);

    return (
        <div className={classes.root}>
            <Paper style={{ backgroundColor: 'white' }}>
                <EnhancedTableToolbar numSelected={selected.length} addDemand={props.addDemand} />
                <div className={classes.tableWrapper}>

                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                variant="outlined"
                                label="Customer"
                                className={classes.search}
                                onChange={({ target }) => setCustomerNameSearch(target.value)}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                variant="outlined"
                                label="Category"
                                className={classes.search}
                                onChange={({ target }) => setCategorySearch(target.value)}
                            />
                        </Grid>


                      
                            <Grid item xs={6}>
                                <FormControl variant="outlined" className={classes.search}>
                                    <InputLabel id="month-search-label">Month</InputLabel>
                                    <Select
                                        labelId="month-search-label"
                                        value={selectedMonth}
                                        onChange={({ target }) => setSelectedMonth(target.value)}
                                        label="Month"
                                    >
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                                            <MenuItem key={month} value={month}>{month}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl variant="outlined" className={classes.search}>
                                    <InputLabel id="year-search-label">Year</InputLabel>
                                    <Select
                                        labelId="year-search-label"
                                        value={selectedYear}
                                        onChange={({ target }) => setSelectedYear(target.value)}
                                        label="Year"
                                    >
                                        {Array.from({ length: new Date().getFullYear() - 2020 + 1 }, (_, i) => i + 2020).map((year) => (
                                            <MenuItem key={year} value={year}>{year}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
        

                        <Grid item xs={6}>
                            <FormControl variant="outlined" className={classes.search}>
                                <InputLabel id="active-search-label">Finished</InputLabel>
                                <Select
                                    labelId="active-search-label"
                                    value={finishSearch}
                                    onChange={({ target }) => setFinishSearch(target.value)}
                                    label="Finished"
                                >
                                    <MenuItem value={'Yes'}>Yes</MenuItem>
                                    <MenuItem value={'No'}>No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                    </Grid>
                    <Table
                        className={classes.table}
                        aria-labelledby="tableTitle"
                        size={'medium'}
                    >
                        <EnhancedTableHead
                            numSelected={selected.length}
                            order={order}
                            orderBy={orderBy}
                            onSelectAllClick={handleSelectAllClick}
                            onRequestSort={handleRequestSort}
                            rowCount={listDemands.length}
                        />
                        <TableBody>
                            {stableSort(listDemands, getSorting(order, orderBy))
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, index) => {
                                    const isItemSelected = isSelected(row.customerName);
                                    const labelId = `enhanced-table-checkbox-${index}`;

                                    return (
                                        <TableRow
                                            hover
                                            role="checkbox"
                                            aria-checked={isItemSelected}
                                            tabIndex={-1}
                                            key={row.id}
                                            selected={isItemSelected}>
                                            <TableCell component="th" id={labelId} scope="row">
                                                {row.customerName}
                                            </TableCell>
                                            <TableCell>{row.customerContact}</TableCell>
                                            <TableCell>{row.state}</TableCell>
                                            <TableCell>{row.category}</TableCell>
                                            <TableCell>{formatDateToAmerican(row.creationDate)}</TableCell>
                                            <TableCell>{row.finished ? 'Yes' : 'No'}</TableCell>
                                            <TableCell>
                                                {/*<Tooltip title="Edit demand" aria-label="Edit demand">
                                                    <IconButton onClick={() => {
                                                        props.chooseDemand(row);
                                                        props.addDemand();
                                                    }}>
                                                        <Icon color="primary">edit</Icon>
                                                    </IconButton>
                                                </Tooltip>*/}
                                                <Tooltip title="Delete demand" aria-label="Delete demand">
                                                    <IconButton onClick={() => {
                                                        showModalDelete(row);
                                                    }}>
                                                        <Icon color="secondary">delete</Icon>
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Finish demand" aria-label="Finish demand">
                                                    <IconButton onClick={() => {
                                                        showModalFinish(row);
                                                    }}>
                                                        <Icon color="primary">check</Icon>
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            {emptyRows > 0 && (
                                <TableRow style={{ height: 49 * emptyRows }}>
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
                <TablePagination
                    rowsPerPageOptions={[5, 10]}
                    component="div"
                    count={listDemands.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    backIconButtonProps={{
                        'aria-label': 'Previous Page',
                    }}
                    nextIconButtonProps={{
                        'aria-label': 'Next Page',
                    }}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </Paper>
            <ConfirmDeleteDemand showConfirm={showDeleteConfirmation}
                demand={selectedDemand}
                onClose={() => {
                    closeModalDelete();
                }} />

            <ConfirmFinishDemand showConfirm={showFinishConfirmation}
                demand={selectedDemand}
                onClose={() => {
                    closeModalFinish();
                }} />
        </div>
    );
}


const mapDispatchToProps = dispatch => (
    bindActionCreators({ cleanDemand, chooseDemand, loadDemands }, dispatch)
);

export default connect(null, mapDispatchToProps)(DemandsTable);